import React from "react"
import { observer } from "mobx-react-lite"

import Button from "@components/ui/Button/Button"
import Text from "@components/ui/Typography/Text"
import { useAppConfig } from "@components/theme/AppConfigProvider"
import { useStore } from "@store/index"
import userService from "@services/user.service"
import {
  copilotStudioClientId,
  copilotStudioDirectConnectUrl,
  copilotStudioEnvironmentId,
  copilotStudioSchemaName,
} from "@framework/constants/copilot-studio"

import {
  beginCopilotStudioSignIn,
  clearCopilotStudioToken,
  completeCopilotStudioSignIn,
  copilotSilentSignInNeedsInteraction,
  copilotStudioAuthMode,
  readCopilotLoginHint,
  readCopilotStudioToken,
  refreshCopilotStudioToken,
  rememberCopilotLoginHint,
} from "./copilotStudioAuth"
import {
  agentRepliesFromActivities,
  CopilotCitation,
  sendCopilotMessage,
  startCopilotConversation,
} from "./copilotStudioClient"

import CopilotMessageBody from "./formatCopilotMessage"
import {
  CopilotBrandScope,
  copilotBrandActivityValue,
  restrictCopilotReply,
  scopeCopilotQuestion,
} from "./copilotBrandScope"
import styles from "./CopilotStudioSdkChat.module.sass"

type ChatLine = {
  id: string
  role: "user" | "agent"
  text: string
  citations?: CopilotCitation[]
}

const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024

function readFileAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ""))
    reader.onerror = () => reject(new Error("Could not read the file"))
    reader.readAsDataURL(file)
  })
}

const CopilotStudioSdkChat: React.FC = observer(() => {
  const theme = useAppConfig()
  const { userStore } = useStore()
  const clientId =
    copilotStudioClientId || theme.services.microsoft?.clientId || ""
  const loginHint = userStore.user?.email || readCopilotLoginHint()
  const loginHintRef = React.useRef(loginHint)
  loginHintRef.current = loginHint

  const [token, setToken] = React.useState<string | null>(() =>
    readCopilotStudioToken()
  )
  const [conversationId, setConversationId] = React.useState("")
  const [lines, setLines] = React.useState<ChatLine[]>([])
  const [draft, setDraft] = React.useState("")
  const [file, setFile] = React.useState<File | null>(null)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState("")
  const [needsManualSignIn, setNeedsManualSignIn] = React.useState(false)
  const [brandScope, setBrandScope] = React.useState<CopilotBrandScope | null>(null)
  const [scopeReady, setScopeReady] = React.useState(false)
  const brandScopeRef = React.useRef<CopilotBrandScope | null>(null)
  brandScopeRef.current = brandScope
  const started = React.useRef(false)
  const authStarted = React.useRef(false)
  const listRef = React.useRef<HTMLDivElement>(null)
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const connection = React.useMemo(
    () => ({
      environmentId: copilotStudioEnvironmentId,
      schemaName: copilotStudioSchemaName,
      directConnectUrl: copilotStudioDirectConnectUrl,
      token: token || "",
    }),
    [token]
  )

  const appendAgentLines = (activities: Parameters<typeof agentRepliesFromActivities>[0]) => {
    const next = agentRepliesFromActivities(activities).map((reply) => ({
      id: `${Date.now()}-${Math.random()}`,
      role: "agent" as const,
      text: reply.text,
      citations: reply.citations,
    }))
    const visible = next.map((reply) => restrictCopilotReply(reply, brandScopeRef.current))
    if (visible.length) {
      setLines((current) => [...current, ...visible])
      return
    }
    setError("The agent returned no reply. Confirm LabWizard Standard Agent is published, then sign out and sign in again.")
  }

  React.useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight })
  }, [lines, busy])

  React.useEffect(() => {
    if (!clientId || token) return undefined
    rememberCopilotLoginHint(loginHintRef.current)

    const params = new URLSearchParams(window.location.search)
    const authError = params.get("error")
    const authErrorDescription = params.get("error_description")
    const code = params.get("code")

    if (code) {
      window.history.replaceState({}, "", "/copilot")
      let cancelled = false
      setBusy(true)
      completeCopilotStudioSignIn(clientId, code)
        .then((accessToken) => {
          if (!cancelled) {
            setNeedsManualSignIn(false)
            setToken(accessToken)
          }
        })
        .catch((signInError: Error) => {
          if (!cancelled) {
            setNeedsManualSignIn(true)
            setError(signInError.message)
          }
        })
        .finally(() => {
          if (!cancelled) setBusy(false)
        })
      return () => {
        cancelled = true
      }
    }

    if (authError) {
      window.history.replaceState({}, "", "/copilot")
      if (
        copilotStudioAuthMode() === "silent" &&
        copilotSilentSignInNeedsInteraction(authError)
      ) {
        beginCopilotStudioSignIn(clientId, {
          silent: false,
          loginHint: loginHintRef.current,
        }).catch((signInError: Error) => {
          setNeedsManualSignIn(true)
          setError(signInError.message)
        })
        return undefined
      }
      setNeedsManualSignIn(true)
      setError(authErrorDescription || authError)
      return undefined
    }

    if (authStarted.current) return undefined
    authStarted.current = true
    let cancelled = false
    setBusy(true)
    refreshCopilotStudioToken(clientId)
      .then((accessToken) => {
        if (cancelled) return
        if (accessToken) {
          setToken(accessToken)
          setBusy(false)
          return
        }
        return beginCopilotStudioSignIn(clientId, {
          silent: true,
          loginHint: loginHintRef.current,
        })
      })
      .catch((signInError: Error) => {
        if (!cancelled) {
          setNeedsManualSignIn(true)
          setError(signInError.message)
          setBusy(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [clientId, token])

  React.useEffect(() => {
    if (!token) {
      setBrandScope(null)
      setScopeReady(false)
      return undefined
    }
    let cancelled = false
    userService
      .getCopilotBrandScope()
      .then((response) => {
        if (cancelled) return
        setBrandScope(response.data.data)
        setScopeReady(true)
      })
      .catch(() => {
        if (cancelled) return
        setScopeReady(false)
        setError(
          "Could not load your brand access. Copilot stays closed so other brands are not shown."
        )
      })
    return () => {
      cancelled = true
    }
  }, [token])

  React.useEffect(() => {
    if (!token || !scopeReady || started.current) return undefined
    started.current = true
    let cancelled = false
    setBusy(true)
    setError("")
    startCopilotConversation(connection)
      .then((result) => {
        if (cancelled) return
        if (result.conversationId) setConversationId(result.conversationId)
        appendAgentLines(result.activities)
      })
      .catch((startError: Error) => {
        if (!cancelled) {
          started.current = false
          setError(startError.message)
        }
      })
      .finally(() => {
        if (!cancelled) setBusy(false)
      })
    return () => {
      cancelled = true
    }
  }, [token, connection, scopeReady])

  const onSignIn = () => {
    setError("")
    setNeedsManualSignIn(false)
    beginCopilotStudioSignIn(clientId, {
      silent: false,
      loginHint,
    }).catch((signInError: Error) => {
      setNeedsManualSignIn(true)
      setError(signInError.message)
    })
  }

  const onSignOut = () => {
    clearCopilotStudioToken()
    setToken(null)
    setConversationId("")
    setLines([])
    started.current = false
  }

  const onSend = async () => {
    const text = draft.trim()
    if ((!text && !file) || !token || busy) return
    if (!conversationId) {
      setError("The agent conversation did not start. Sign out and sign in again.")
      return
    }
    setBusy(true)
    setError("")
    let attachment:
      | { name: string; contentType: string; contentUrl: string }
      | undefined
    try {
      if (file) {
        attachment = {
          name: file.name,
          contentType: file.type || "application/octet-stream",
          contentUrl: await readFileAsDataUrl(file),
        }
      }
    } catch (readError) {
      setBusy(false)
      setError(readError instanceof Error ? readError.message : "Could not read the file")
      return
    }
    setLines((current) => [
      ...current,
      {
        id: `user-${Date.now()}`,
        role: "user",
        text: text || `Attached ${file?.name}`,
      },
    ])
    setDraft("")
    setFile(null)
    try {
      const result = await sendCopilotMessage({
        ...connection,
        conversationId,
        text: scopeCopilotQuestion(
          text || "Please use the attached file.",
          brandScopeRef.current
        ),
        value: copilotBrandActivityValue(brandScopeRef.current),
        attachment,
      })
      if (result.conversationId) setConversationId(result.conversationId)
      appendAgentLines(result.activities)
    } catch (sendError) {
      setError(sendError instanceof Error ? sendError.message : "Send failed")
    } finally {
      setBusy(false)
    }
  }

  if (!clientId) {
    return (
      <div className={styles.empty}>
        <Text variant="h3" as="h1">
          Microsoft sign-in is not configured
        </Text>
        <Text variant="body2" color="text70Color">
          Set REACT_APP_COPILOT_STUDIO_CLIENT_ID to an Entra app registration
          with delegated CopilotStudio.Copilots.Invoke, and add this redirect
          URI: {window.location.origin}/copilot
        </Text>
      </div>
    )
  }

  if (!token) {
    return (
      <div className={styles.empty}>
        <Text variant="h3" as="h1">
          LabWizard Standard Agent
        </Text>
        <Text variant="body2" color="text70Color">
          {needsManualSignIn
            ? "Microsoft could not reuse your portal sign-in. Continue once so SharePoint answers use your own access."
            : "Using your portal sign-in for SharePoint access."}
        </Text>
        {error ? (
          <Text variant="body2" color="redColor">
            {error}
          </Text>
        ) : null}
        {needsManualSignIn ? (
          <Button color="primary" disabled={busy} onClick={onSignIn}>
            Continue with Microsoft
          </Button>
        ) : (
          <Text variant="body2">Signing you in…</Text>
        )}
      </div>
    )
  }

  return (
    <div className={styles.chat}>
      <div className={styles.toolbar}>
        <Text variant="body2">LabWizard Standard Agent · your SharePoint access</Text>
        <Button variant="outlined" size="small" color="primary" onClick={onSignOut}>
          Sign out
        </Button>
      </div>
      <div className={styles.messages} ref={listRef}>
        {!scopeReady && !error ? (
          <div className={styles.agent}>Checking your brand access…</div>
        ) : null}
        {lines.map((line) => (
          <div
            key={line.id}
            className={line.role === "user" ? styles.user : styles.agent}
          >
            {line.role === "agent" ? (
              <CopilotMessageBody text={line.text} />
            ) : (
              line.text
            )}
            {line.citations?.length ? (
              <div className={styles.sources}>
                {line.citations.map((citation) =>
                  citation.url ? (
                    <a
                      key={citation.url}
                      href={citation.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {citation.title}
                    </a>
                  ) : (
                    <span key={citation.title}>{citation.title}</span>
                  )
                )}
              </div>
            ) : null}
          </div>
        ))}
        {busy ? <div className={styles.agent}>Working…</div> : null}
      </div>
      {error ? (
        <Text variant="caption1" color="redColor" className={styles.error}>
          {error}
        </Text>
      ) : null}
      <form
        className={styles.composer}
        onSubmit={(event) => {
          event.preventDefault()
          onSend()
        }}
      >
        {/* file input is the control; visually hidden beside this label */}
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label className={styles.file} htmlFor="copilot-file">
          Attach
        </label>
        <input
          id="copilot-file"
          ref={fileInputRef}
          className={styles.fileInput}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.docx,.txt,.csv"
          onChange={(event) => {
            const next = event.currentTarget.files?.[0]
            if (next && next.size > MAX_ATTACHMENT_BYTES) {
              setError("File must be 15 MB or smaller.")
              if (fileInputRef.current) fileInputRef.current.value = ""
              setFile(null)
              return
            }
            setFile(next || null)
          }}
        />
        {file ? <span className={styles.fileName}>{file.name}</span> : null}
        <input
          className={styles.input}
          value={draft}
          placeholder="Ask LabWizard Standard Agent"
          onChange={(event) => setDraft(event.target.value)}
        />
        <Button color="primary" size="medium" type="submit" disabled={busy}>
          Send
        </Button>
      </form>
    </div>
  )
})

export default CopilotStudioSdkChat
